<template>
  <tr
    class="group hover:bg-blue-50/50 transition-colors duration-200 border-b border-gray-100"
  >
    <!-- Days Column -->
    <td class="py-3 px-4 text-right  text-gray-900">
      {{ australianNumber($props.wbsCode.days) }}
    </td>

    <td class="py-3 px-2 text-right text-gray-900">
      {{ australianNumber($props.toComplete.toDateDays) }}
    </td>

    <td class="py-3 px-4 text-right  text-gray-900">
      {{ australianNumber($props.toComplete.toCompleteDays) }}
    </td>

    <!-- Production Rate Column -->
    <td class="py-3 px-4 text-right  text-gray-900 border-l border-gray-300">
      {{ australianNumber($props.wbsCode.tender_production_rate) }}{{ $props.wbsCode.unit }}/day
    </td>

    <td class="py-3 px-2 text-right text-gray-900">
      {{ australianNumber($props.toComplete.toDateProductionRate) }}{{ $props.wbsCode.unit }}/day
    </td>

    <td
      v-if="typingProductionRate"
      class="py-3 px-4"
    >
      <div class="flex items-center justify-end space-x-2">
        <form
          action=""
          class="flex items-center space-x-2"
          @submit.prevent="updateWbsCode"
        >
          <AppInput
            ref="productionRateInput"
            v-model="toCompleteProductionRate"
            name="to_complete_production_rate"
            type="number"
            step="any"
            min="0"
            class="form-input w-24 text-right h-7"
            data-test="wbs-code-final-quantity"
            :title="$t('CostAtComplete.WbsCodeRowDetails.productionRateTooltip')"
            @blur="updateWbsCode"
            @keyup.enter="updateWbsCode"
            @keyup.esc="typingProductionRate = false"
          />

          <div class="text-sm text-gray-500">
            {{ $props.wbsCode.unit }}/day
          </div>
        </form>

        <button
          type="button"
          class="btn-icon btn-icon-green inline-flex"
          :title="$t('CostAtComplete.WbsCodeRowDetails.saveButton')"
          @click="updateWbsCode"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
        </button>

        <button
          type="button"
          class="btn-icon btn-icon-red inline-flex"
          :title="$t('CostAtComplete.WbsCodeRowDetails.cancelButton')"
          @click="typingProductionRate = false"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
        </button>
      </div>
    </td>

    <td
      v-else-if="projectsStore.project?.unarchived"
      class="py-3 px-4 text-right cursor-pointer"
      data-test="record-paid-text"
      @click.stop="toggletypingProductionRate"
    >
      <div class="flex justify-end items-center space-x-2  px-2 py-1 rounded transition-colors">
        <button
          type="button"
          class="btn-icon btn-icon-blue transition-colors opacity-0 group-hover:opacity-100 duration-200"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <span class=" text-gray-900">
          {{ australianNumber($props.toComplete.toCompleteProductionRate) }}{{ $props.wbsCode.unit }}/day
        </span>
      </div>
    </td>

    <td
      v-else
      class="py-3 px-4 text-right"
    >
      <div class="flex justify-end items-center">
        <span class=" text-gray-900">
          {{ australianNumber($props.toComplete.toCompleteProductionRate) }}{{ $props.wbsCode.unit }}/day
        </span>
      </div>
    </td>

    <!-- Quantities Column -->
    <td class="py-3 px-4 text-right  text-gray-900  border-l border-gray-300">
      {{ australianNumber($props.wbsCode.tender_quantity) }}{{ $props.wbsCode.unit }}
    </td>

    <td class="py-3 px-2 text-right text-gray-900">
      {{ australianNumber($props.toComplete.toDateQuantity) }}{{ $props.wbsCode.unit }}
    </td>

    <td
      class="py-3 px-4 text-right"
      :class="{
        'bg-red-50': $props.toComplete.toCompleteQuantity < 0,
        'bg-green-50': $props.toComplete.toCompleteQuantity >= 0 && $props.toComplete.toCompleteQuantity > 0
      }"
    >
      <div class="flex justify-end items-center">
        <span
          class=""
          :class="{
            'text-red-700': $props.toComplete.toCompleteQuantity < 0,
            'text-green-700': $props.toComplete.toCompleteQuantity >= 0 && $props.toComplete.toCompleteQuantity > 0,
            'text-gray-900': $props.toComplete.toCompleteQuantity === 0
          }"
        >
          {{ australianNumber($props.toComplete.toCompleteQuantity) }}{{ $props.wbsCode.unit }}
        </span>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianNumber } from '@/helpers';
import WbsCode from '@/models/WbsCode';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { nextTick, ref } from 'vue';

const props = defineProps<{
  wbsCode: WbsCode;
  toComplete: CostAtCompletion;
}>();

const typingProductionRate = ref(false);
const wbsCodesStore = useWbsCodesStore();
const saveProductionRate = useStoreApiAction(wbsCodesStore.updateCode);

const toCompleteProductionRate = ref(
  props.wbsCode.to_complete_production_rate || Math.round(props.toComplete.toCompleteProductionRate),
);

const updateWbsCode = () => {
  if(toCompleteProductionRate.value > 0) {
    saveProductionRate
      .request(props.wbsCode.id, { to_complete_production_rate: toCompleteProductionRate.value })
      .then(() => {
        typingProductionRate.value = false;
      });
  } else {
    toCompleteProductionRate.value = props.wbsCode.to_complete_production_rate ||
      Math.round(props.toComplete.toCompleteProductionRate);

    typingProductionRate.value = false;
  }
};

const productionRateInput = ref();

const toggletypingProductionRate = () => {
  typingProductionRate.value = !typingProductionRate.value;

  if(typingProductionRate.value) {
    nextTick(() => {
      productionRateInput.value?.focusInput();
    });
  }
};

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
